import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';

import { Observable, map, take } from 'rxjs';

import { BookingLocationCodes } from '@fcom/booking';
import { formatTime } from '@fcom/common/pipes/time.pipe';
import { ScrollHandleContainerComponent } from '@fcom/common/components/scroll-handle-container/scroll-handle-container.component';
import { Step } from '@fcom/common/interfaces';
import { RootPaths, TripType } from '@fcom/core/constants';
import { LoginGender } from '@fcom/core-api/login';
import {
  Category,
  FinnairBoundItem,
  FinnairCabinClass,
  FinnairDisruptedBoundItem,
  FinnairItineraryItem,
  FinnairItineraryItemFlight,
  FinnairItineraryItemFlightStatus,
  FinnairItineraryItemType,
  FinnairLocation,
  FinnairOrder,
  FinnairOtherInformation,
  FinnairPassengerServiceSelectionItem,
  FinnairServiceCatalogEligibilityCategory,
  FinnairServiceCatalogItemV3,
  FinnairTotalPricesDetails,
  FinnairTravelEndpoint,
  ItineraryItem,
  ItineraryItemFlight,
  ItineraryItemLayover,
  FinnairGender,
  FinnairBoundGroup,
} from '@fcom/dapi/api/models';
import { Amount, DapiHttpErrorResponse, LegEndpoint } from '@fcom/dapi/interfaces';
import { Airport } from '@fcom/ui-components';
import { DateFormat, isFinnairNorraOrWetLease, LocalDate, rangeFrom } from '@fcom/utils';
import { matchesLastPathSegment, pathIsWithinHardcodedPath } from '@fcom/utils/app-url-utils';
import { Bound } from '@fcom/dapi/api/models/bound';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';

import { BUS_AIRCRAFT_CODE, CartOrOrder, FinnairBoundItemWithLocation, GENDER_OPTIONS, Title } from '../interfaces';
import { isBoundBasedCategory } from '../modules/ancillaries/utils/category.utils';

export const toAmount = (amount): Amount => ({
  amount: String(amount.amount),
  currencyCode: amount.currencyCode,
});

export const isInBookingPurchase = (url: string): boolean =>
  pathIsWithinHardcodedPath(url, RootPaths.BOOKING_ROOT) &&
  (matchesLastPathSegment(url, BOOKING_STEPS.PURCHASE_SUCCESS.path) ||
    matchesLastPathSegment(url, BOOKING_STEPS.CHECKOUT.path));

export const isInMmb = (url: string): boolean =>
  pathIsWithinHardcodedPath(url, RootPaths.MANAGE_BOOKING_ROOT) &&
  !matchesLastPathSegment(url, RootPaths.MANAGE_BOOKING_ROOT);

export const isMmbStep = (url: string, step: Step): boolean =>
  pathIsWithinHardcodedPath(url, RootPaths.MANAGE_BOOKING_ROOT) && matchesLastPathSegment(url, step.path);

export const isChangeFlowStep = (url: string, step: Step): boolean =>
  url.includes([RootPaths.VOLUNTARY_CHANGE_ROOT, step.path].join('/'));

export const isBoundFlown = (bound: FinnairBoundItem | FinnairDisruptedBoundItem): boolean =>
  allFlightsHaveStatuses(bound, [FinnairItineraryItemFlightStatus.FLOWN]);

export const isBoundOrFlightConfirmedOrCheckedIn = (
  boundOrFlight: FinnairBoundItem | FinnairItineraryItemFlight
): boolean => {
  const confirmedOrCheckedIn = [
    FinnairItineraryItemFlightStatus.CONFIRMED,
    FinnairItineraryItemFlightStatus.CHECKED_IN,
  ];
  return isBound(boundOrFlight)
    ? allFlightsHaveStatuses(boundOrFlight, confirmedOrCheckedIn)
    : confirmedOrCheckedIn.includes(boundOrFlight.status);
};

const allFlightsHaveStatuses = (
  bound: FinnairBoundItem | FinnairDisruptedBoundItem,
  statuses: FinnairItineraryItemFlightStatus[]
): boolean => bound.itinerary.filter(isFlight).every((i) => statuses.includes(i.status));

export const isBound = (
  toBeDetermined: FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight | Bound
): toBeDetermined is FinnairBoundItem | FinnairDisruptedBoundItem | Bound =>
  toBeDetermined && !!(toBeDetermined as FinnairBoundItem | FinnairDisruptedBoundItem | Bound).itinerary;

const asTimeAndWeekDay = (dateTime: string, translations: unknown): string =>
  `${formatTime(dateTime)} ${new DateFormat(translations).format(dateTime, DateFormat.WEEKDAY_SHORT)}`;

export const legEndpointToUfoAirport = (
  legEndpoint: LegEndpoint | FinnairTravelEndpoint,
  translations: unknown,
  locations?: { [key: string]: FinnairLocation }
): Airport => {
  if (!legEndpoint) {
    return undefined;
  }
  const { dateTime, locationCode } = legEndpoint;
  const location = locations?.[locationCode];
  return {
    dateTime: asTimeAndWeekDay(dateTime, translations),
    airportCode: locationCode,
    city: location?.cityName ?? (legEndpoint as LegEndpoint).cityName,
    airport: location?.name ?? (legEndpoint as LegEndpoint).name,
  };
};

/**
 * Only feasible for cart model checking
 * @param totalPrices
 */
export const isAwardBooking = (totalPrices: FinnairTotalPricesDetails): boolean =>
  !!totalPrices?.total?.total?.totalPoints;

export const numberOfDaysInMonth = (year: number): number[] => {
  return rangeFrom(1, 12).map((m) => LocalDate.getAmountOfDaysInMonth(year, m));
};

export const operatingAirlineCodeOrBusIndicator = (flight: FinnairItineraryItemFlight): string =>
  isBus(flight) ? BUS_AIRCRAFT_CODE.toLowerCase() : flight.operatingAirline?.code;

const isBus = (flight: FinnairItineraryItemFlight): boolean =>
  flight.aircraft?.code?.toUpperCase() === BUS_AIRCRAFT_CODE;

export const getTails = (
  boundOrFlight: FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight
): string[] => {
  const tails: string[] = isBound(boundOrFlight)
    ? boundOrFlight.itinerary
        .filter(isFlight)
        .flatMap((itinerary: FinnairItineraryItemFlight) => operatingAirlineCodeOrBusIndicator(itinerary))
    : [operatingAirlineCodeOrBusIndicator(boundOrFlight)];
  return tails.filter((tail) => tail);
};

export const isByBusOnly = (
  boundOrFlight: FinnairBoundItem | FinnairDisruptedBoundItem | FinnairItineraryItemFlight
): boolean => {
  return isBound(boundOrFlight)
    ? boundOrFlight.itinerary.filter(isFlight).every((itinerary: FinnairItineraryItemFlight) => isBus(itinerary))
    : isBus(boundOrFlight);
};

export function isUsingSAF(
  boundOrFlight:
    | FinnairBoundItemWithLocation
    | FinnairBoundItem
    | FinnairDisruptedBoundItem
    | Bound
    | FinnairItineraryItemFlight
): boolean {
  return isBound(boundOrFlight)
    ? boundOrFlight.itinerary.some((itineraryItem) => isSegmentUsingSAF(itineraryItem))
    : isSegmentUsingSAF(boundOrFlight);
}

export function isSegmentUsingSAF(segment: FinnairItineraryItem | ItineraryItemFlight): boolean {
  if (!isFlight(segment)) {
    return false;
  }
  const opAirline = segment.operatingAirline;
  return !isBus(segment) && isFinnairNorraOrWetLease(opAirline.name, opAirline.code);
}

/**
 * Priority services aren't available if first leg is on bus and second is code share flight
 * @param itineraries
 * @returns {boolean}
 */
export const priorityServicesAvailable = (itineraries: (ItineraryItemFlight | ItineraryItemLayover)[]): boolean => {
  const legs: FinnairItineraryItemFlight[] = itineraries.filter(isFlight);
  if (legs.length <= 1) {
    return true;
  }
  const airLine = legs[1].operatingAirline;
  return !isByBusOnly(legs[0]) || isFinnairNorraOrWetLease(airLine?.name, airLine?.code);
};

export const getDapiErrorKey = (err: unknown): string => {
  const error = (err as DapiHttpErrorResponse)?.error;
  return `${error?.key ?? 'UNKNOWN_ERROR'}_${error?.errorType ?? 'UNKNOWN_TYPE'}`;
};

export const loginGenderToGenderOption = (loginGender: LoginGender): string => {
  const genderMap = {
    [LoginGender.MALE]: GENDER_OPTIONS[0],
    [LoginGender.FEMALE]: GENDER_OPTIONS[1],
  };

  return genderMap[loginGender];
};

export const finnairGenderToGenderOption = (finnairGender: FinnairGender): 'mr' | 'ms' => {
  const genderMap = {
    [FinnairGender.MALE]: GENDER_OPTIONS[0],
    [FinnairGender.FEMALE]: GENDER_OPTIONS[1],
  };

  return genderMap[finnairGender];
};

export const titleToLoginGender = (title: string): LoginGender => {
  const genderMap = {
    [Title.MR]: LoginGender.MALE,
    [Title.MS]: LoginGender.FEMALE,
    [Title.MRS]: LoginGender.FEMALE,
  };

  return genderMap[title];
};

export const getCabinClassShortCode = (cabinClass: string): string => {
  const map = {
    [FinnairCabinClass.ECONOMY]: 'E',
    [FinnairCabinClass.ECOPREMIUM]: 'PE',
    [FinnairCabinClass.BUSINESS]: 'B',
    [FinnairCabinClass.FIRST]: 'F',
  };
  return map[cabinClass] || '';
};

export const getBrandNameShortCode = (brandName: string): string => {
  const name = brandName?.toUpperCase() || '';
  if (name.includes('LIGHT')) {
    return 'L';
  } else if (name.includes('CLASSIC')) {
    return 'C';
  } else if (name.includes('FLEX')) {
    return 'F';
  }
  return '';
};

export const adjustFareFamilyOptionHeight = (
  scrollContainer: ScrollHandleContainerComponent,
  fareHeaderSelectors: string,
  property: string
): void => {
  if (!scrollContainer) {
    return;
  }

  const fareHeaders: HTMLElement[] = Array.from<HTMLElement>(
    scrollContainer.container.nativeElement.querySelectorAll(fareHeaderSelectors)
  );
  fareHeaders.forEach((e) => {
    e.style[property] = 'auto';
  });

  const itemHeights = fareHeaders.map((e) => e.getBoundingClientRect().height);
  const maxHeight = Math.max(...itemHeights);
  fareHeaders.forEach((e) => {
    e.style[property] = `${maxHeight}px`;
  });
};

export const getTripTypeFromBookingLocationCodes = (locations: BookingLocationCodes[]): TripType => {
  if (locations.length === 1) {
    return TripType.ONEWAY;
  }

  if (
    locations.length === 2 &&
    locations[0].originLocationCode === locations[1].destinationLocationCode &&
    locations[0].destinationLocationCode === locations[1].originLocationCode
  ) {
    return TripType.RETURN;
  }

  return TripType.MULTICITY;
};

export const getTripTypeFromBounds = (bounds: FinnairBoundItem[]): TripType => {
  return getTripTypeFromBookingLocationCodes(
    bounds.map((bound) => {
      return {
        originLocationCode: bound.departure.locationCode,
        destinationLocationCode: bound.arrival.locationCode,
      };
    })
  );
};

/**
 * Determine if there is cancelation with no alternative
 * @param bound
 */
export const isCancelledFlightWithNoAlternative = (bound: FinnairBoundItem): boolean => {
  return (
    bound.itinerary
      .filter((flight) => flight.type === FinnairItineraryItemType.FLIGHT)
      .some((flight: FinnairItineraryItemFlight) => flight.status === FinnairItineraryItemFlightStatus.CANCELED) &&
    !bound.disruptedBound
  );
};

/**
 * Determine if there is disrupted bound in the booking
 * @param booking Information about the whole booking
 */
export const bookingHasDisruption = (booking: FinnairOrder): boolean =>
  (booking?.eligibilities.acknowledge.some((eligility) => eligility.isAllowedToUse) ||
    booking?.bounds.some((bound) => isCancelledFlightWithNoAlternative(bound))) ??
  false;

export const bookingHasCoverService = (booking: FinnairOrder): boolean =>
  booking?.services?.included.some((includedService) => includedService.category === Category.COVER);

export const getAllFlightsFromCartOrOrder = (cartData: CartOrOrder): FinnairItineraryItemFlight[] =>
  cartData.bounds?.reduce((all, bound) => all.concat(bound.itinerary.filter(isFlight)), []);

export const isTourOperator = (hasOtherInformation: { otherInformation: FinnairOtherInformation }): boolean =>
  !!hasOtherInformation?.otherInformation?.tourOperator;

export const isNotIncludedService = (
  service: FinnairPassengerServiceSelectionItem
): service is FinnairPassengerServiceSelectionItem => {
  return !service.includedInTicketType && !service.includedInTierBenefit;
};

export const isFlight = (value: FinnairItineraryItem | ItineraryItem): value is FinnairItineraryItemFlight => {
  return value.type === FinnairItineraryItemType.FLIGHT;
};

export const hasBusSegment = (order: FinnairOrder): boolean =>
  order.bounds.some((bound) =>
    bound.itinerary.filter(isFlight).some((i: FinnairItineraryItemFlight) => isByBusOnly(i))
  );

const isCatalogHavingService = (
  servicesToSell: FinnairServiceCatalogItemV3['services'],
  eligibilities: FinnairServiceCatalogEligibilityCategory['services'],
  bound: FinnairBoundItem
): boolean => {
  const servicesForPaxes = servicesToSell[bound.id]
    ? [servicesToSell[bound.id]]
    : bound.itinerary
        .filter(isFlight)
        .map((flight) => servicesToSell[flight.id])
        .filter(Boolean);

  return servicesForPaxes.some((servicesForPax) => {
    return Object.entries(servicesForPax).some(([paxId, paxServices]) => {
      return paxServices.length > 0 && isBoundHavingEligibilityForPax(eligibilities, bound, paxId);
    });
  });
};

const isBoundHavingEligibilityForPax = (
  eligibilities: FinnairServiceCatalogEligibilityCategory['services'],
  bound: FinnairBoundItem,
  paxId: string
): boolean => {
  const realEligibilities = eligibilities[bound.id]
    ? [eligibilities[bound.id]]
    : bound.itinerary
        .filter(isFlight)
        .map((flight) => eligibilities[flight.id])
        .filter(Boolean);
  return realEligibilities.some((eligibilityForPax) => eligibilityForPax[paxId]?.isAllowedToUse);
};

export const isBoundAirDiscount = (bound: FinnairBoundGroup): boolean => Boolean(bound.originalCheapestPrice);

export const shouldWeSellForCategory = (category: Category, order: FinnairOrder, bound?: FinnairBoundItem): boolean => {
  const servicesToSell = order.serviceCatalog?.categories?.find((c) => c.category === category)?.services ?? {};
  const eligibilities =
    order.eligibilities.serviceCatalog?.categories?.find((c) => c.category === category)?.services ?? {};

  const bounds = bound ? [bound] : order.bounds;

  return bounds.some((currentBound) => isCatalogHavingService(servicesToSell, eligibilities, currentBound));
};

export const allPassengersDontHaveCategoryService = (
  category: Category,
  order: FinnairOrder,
  bound?: FinnairBoundItem | FinnairDisruptedBoundItem
): boolean => {
  const eligibilities =
    order.eligibilities.serviceCatalog?.categories?.find((c) => c.category === category)?.services ?? {};

  const fragmentIds = isBoundBasedCategory(category)
    ? bound
      ? [bound.id]
      : order.bounds.map((b) => b.id)
    : (bound ? bound.itinerary.filter(isFlight) : order.bounds.flatMap((b) => b.itinerary).filter(isFlight)).map(
        ({ id }) => id
      );

  const eligibilitiesToUse = Object.entries(eligibilities).reduce((all, [fragmentId, eligibility]) => {
    if (fragmentIds.includes(fragmentId)) {
      all[fragmentId] = eligibility;
    }
    return all;
  }, {});

  const servicesForSegments =
    order.services.included
      ?.find((service) => service.category === category)
      ?.bounds.filter((b) => (bound ? b.id === bound.id : true))
      .flatMap((b) => b.segments) ?? [];

  const noServiceForAllPassengerOnAnySegment = Object.entries(eligibilitiesToUse).every(
    ([flightId, paxEligibilityForFlight]) => {
      const eligiblePaxIds = Object.keys(paxEligibilityForFlight)
        .filter((paxId) => paxEligibilityForFlight[paxId].isAllowedToUse)
        .sort()
        .join('');
      const paxIdsHavingService =
        servicesForSegments
          .find((segment) => segment.id === flightId)
          ?.passengers.filter((p) => p.services.filter(isNotIncludedService).length > 0)
          .flatMap((p) => p.id)
          .sort()
          .join('') ?? '';

      return paxIdsHavingService !== eligiblePaxIds;
    }
  );

  return (
    Object.values(eligibilitiesToUse)
      .flatMap((s) => Object.values(s))
      .some((e) => e.isAllowedToUse) && noServiceForAllPassengerOnAnySegment
  );
};

export const checkIfAdditionalEmailExists = (
  value: string,
  additionalEmails$: Observable<string[]>
): Observable<boolean> => {
  return additionalEmails$.pipe(
    take(1),
    map((emails) => emails?.includes(value))
  );
};

export const additionalEmailValidator = (additionalEmails$: Observable<string[]>): AsyncValidatorFn => {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    return checkIfAdditionalEmailExists(control.value, additionalEmails$).pipe(
      map((result) => {
        return result ? { duplicate: true } : null;
      })
    );
  };
};
